import { useEffect, useRef, useCallback, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import DriveDetector from '../plugins/DriveDetector';

const IRS_RATE = 0.70;

// Incremented each time a trip is saved — consumers can watch this to know when to refresh
let globalTripVersion = 0;
const tripVersionListeners = new Set();

function notifyTripSaved() {
  globalTripVersion++;
  tripVersionListeners.forEach((fn) => fn(globalTripVersion));
}

/**
 * Hook for components that need to refresh when a new trip is saved.
 * Returns a version counter that increments on each saved trip.
 */
export function useTripVersion() {
  const [version, setVersion] = useState(globalTripVersion);
  useEffect(() => {
    tripVersionListeners.add(setVersion);
    return () => tripVersionListeners.delete(setVersion);
  }, []);
  return version;
}

export function useDriveDetection() {
  const { currentUser } = useAuth();
  const listenersRef = useRef([]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !currentUser) return;

    // Start auto-detection
    DriveDetector.startDetection().catch((e) =>
      console.log('DriveDetector not available:', e.message)
    );

    // Save completed trips to Firestore
    const tripEndedListener = DriveDetector.addListener('tripEnded', async (tripData) => {
      try {
        const tripsRef = collection(db, 'users', currentUser.uid, 'trips');
        await addDoc(tripsRef, {
          date: tripData.startTime,
          endTime: tripData.endTime,
          startAddress: '',
          endAddress: '',
          startLat: tripData.startLat,
          startLng: tripData.startLng,
          endLat: tripData.endLat,
          endLng: tripData.endLng,
          distance: tripData.distance,
          durationMinutes: tripData.durationMinutes,
          coordinates: tripData.coordinates || [],
          deduction: (tripData.distance || 0) * IRS_RATE,
          irsRate: IRS_RATE,
          trackingMethod: 'auto-gps',
          purpose: 'Business',
          createdAt: new Date().toISOString(),
        });
        notifyTripSaved();
      } catch (err) {
        console.error('Failed to save auto-detected trip:', err);
      }
    });

    listenersRef.current.push(tripEndedListener);

    return () => {
      listenersRef.current.forEach((l) => {
        if (l && typeof l.remove === 'function') {
          l.remove();
        } else if (l && typeof l.then === 'function') {
          l.then((h) => h.remove());
        }
      });
      listenersRef.current = [];
    };
  }, [currentUser]);
}
